import _ from 'lodash';
import FJPoolStore from '../../../../store/FJPoolStore';
import FJUtil from '../../../../util/FJUtil';
import FJAiMaskCanvas from './FJAiMaskCanvas';
import FJCreationEditUndoRedoManager from './FJCreationEditUndoRedoManager';
import FJMaskCanvas from './FJMaskCanvas';
import FJOriginCanvas from './FJOriginCanvas';
import FJPhotoRemoveBgCanvas, { BACKGROUND_TYPE } from './FJPhotoRemoveBgCanvas';
import FJCanvasUtil from '../../../../util/FJCanvasUtil';

export const MOUSE_EVENT_TYPE = {
    NONE: 'none',
    MASK_DRAW: 'mask-draw',
    MASK_ERASE: 'mask-erase',
    QUICK_SELECTION: 'quick-selection',
};
// 初始化图片在canvas的内边距
export const INIT_IMAGE_PADDING = {
    width: FJUtil.isMobile() ? 16 * 2 : 78 * 2,
    height: FJUtil.isMobile() ? 16 * 3 : 102,
};

export const OFFSET_TOP = FJUtil.isMobile() ? -8 : 30;
// 视图画布
class ViewCanvas {
    _viewCanvas = null;
    _viewCanvasContext = null;

    _originCanvasObject = null;
    _maskCanvasObject = null;
    _aiMaskCanvasObject = null;

    _photoRemoveBgCanvasObject = null;

    _originRect = { x: 0, y: 0, width: 0, height: 0 };
    // 原始图片缩放比例为1时的大小
    _originZoomSize = { width: 0, height: 0 };
    _zoom = 1;

    _mouseEventType = MOUSE_EVENT_TYPE.NONE;

    _isMouseDown = false;
    _mousePosition = { x: 0, y: 0 };
    _brushSize = 15;

    _isMove = false;

    _compareLineScale = 0.3;
    _isSlideCompare = false;

    /**
     * @desc 是否是去背景模式
     */
    _isPhotoRemoveBg = false;

    _initImagePadding = INIT_IMAGE_PADDING;
    _offsetTop = OFFSET_TOP;

    constructor() {
        this.originCanvasObject = new FJOriginCanvas();
        this.maskCanvasObject = new FJMaskCanvas();
    }

    //#region 初始化相关

    async initPhotoRemoveBgCanvas(editImageUrl) {
        if (!this._photoRemoveBgCanvasObject) {
            this._photoRemoveBgCanvasObject = new FJPhotoRemoveBgCanvas();
        }

        await this._photoRemoveBgCanvasObject.init(editImageUrl);
        const originSize = this._photoRemoveBgCanvasObject.getSize();
        this.maskCanvasObject.init(originSize);
    }

    /**
     * @desc 初始化视图画布
     * @param {*} canvas
     * @param {*} originImageUrl
     * @param {*} viewSize
     */
    async init(canvas, editImageUrl, viewSize) {
        this.viewCanvas = canvas;
        this.viewCanvas.width = viewSize.width;
        this.viewCanvas.height = viewSize.height;
        this.viewCanvasContext = this.viewCanvas.getContext('2d');
        if (this.isPhotoRemoveBg) {
            await this.initPhotoRemoveBgCanvas(editImageUrl);
        } else {
            await this.originCanvasObject.init(editImageUrl);
            const originSize = this.originCanvasObject.getSize();
            this.maskCanvasObject.init(originSize);
        }

        this.initRect();
        this.draw();
    }

    async initOriginImage(imageUrl) {
        if (this.isPhotoRemoveBg) {
            await this._photoRemoveBgCanvasObject.initOriginImage(imageUrl);
        } else {
            await this.originCanvasObject.initOriginImage(imageUrl);
        }
    }

    /**
     * @desc 初始化ai mask画布
     * @param {Array} maskData
     */
    async initAiMaskCanvas(maskData, maskId) {
        if (!this.aiMaskCanvasObject) {
            this.aiMaskCanvasObject = new FJAiMaskCanvas();
        }

        const originSize = this.originCanvasObject.getSize();
        const maskDrawSize = {
            width: this.originRect.width,
            height: this.originRect.height,
        };
        await this.aiMaskCanvasObject.init(originSize, maskData, maskId, maskDrawSize, this.getRealCanvasRatio());
    }
    //#endregion

    //#region 鼠标事件相关

    // _quickSelectionDownHandler(maskCanvasPoint) {
    // 选中事件挪到 up 事件中
    // if (!this.aiMaskCanvasObject || !this.aiMaskCanvasObject.getLoaded()) return;
    // // 相对于画面中的 x， y  比例
    // const maskCanvasPointScale = {
    //     x: maskCanvasPoint.x / this.originRect.width,
    //     y: maskCanvasPoint.y / this.originRect.height,
    // };
    // const boundAddUndo = this.addUndo.bind(this);
    // const isUpdateView = this.aiMaskCanvasObject.mouseDownHandler(
    //     maskCanvasPointScale.x,
    //     maskCanvasPointScale.y,
    //     boundAddUndo,
    // );
    // return isUpdateView;
    // }

    _quickSelectionMoveHandler(e, isMouseInToolPanel) {
        if (!this.aiMaskCanvasObject || !this.aiMaskCanvasObject.getLoaded()) return;

        this._mousePosition = this.__getMousePosition(e);

        // 相对于画面中的 x， y  比例
        const maskCanvasPointScale = {
            x: (this._mousePosition.x - this.originRect.x) / this.originRect.width,
            y: (this._mousePosition.y - this.originRect.y) / this.originRect.height,
        };

        const isUpdateView = this.aiMaskCanvasObject.mouseMoveHandler(
            maskCanvasPointScale.x,
            maskCanvasPointScale.y,
            isMouseInToolPanel,
        );

        return isUpdateView;
    }
    _quickSelectionUpHandler(maskCanvasPoint) {
        if (!this.aiMaskCanvasObject || !this.aiMaskCanvasObject.getLoaded()) return;

        // 相对于画面中的 x， y  比例
        const maskCanvasPointScale = {
            x: maskCanvasPoint.x / this.originRect.width,
            y: maskCanvasPoint.y / this.originRect.height,
        };
        const boundAddUndo = this.addUndo.bind(this);
        const isUpdateView = this.aiMaskCanvasObject.mouseUpHandler(
            maskCanvasPointScale.x,
            maskCanvasPointScale.y,
            boundAddUndo,
        );
        return isUpdateView;
    }
    mouseDownHandler(e, { drawStart = true }) {
        this._isMouseDown = true;
        this._mousePosition = this.__getMousePosition(e);

        let updateView = false;

        if (this.isMove) {
            this._moveHandler(e);
            this.draw();
            return;
        }

        if (this._isSlideCompare) {
            return;
        }

        switch (this.mouseEventType) {
            case MOUSE_EVENT_TYPE.MASK_ERASE:
            case MOUSE_EVENT_TYPE.MASK_DRAW: {
                const maskCanvasPoint = {
                    x: this._mousePosition.x - this.originRect.x,
                    y: this._mousePosition.y - this.originRect.y,
                };
                this.maskCanvasObject.startDrawing(maskCanvasPoint);

                // 特殊处理  每次点击都添加撤销  并更新画面
                this.addUndo();
                updateView = true;
                drawStart && this._maskDrawAndEraseHandler(e);
                break;
            }
            // case MOUSE_EVENT_TYPE.QUICK_SELECTION: {
            //     if (mouseInToolPanel) return;
            //     const maskCanvasPoint = {
            //         x: this._mousePosition.x - this.originRect.x,
            //         y: this._mousePosition.y - this.originRect.y,
            //     };
            //     updateView = this._quickSelectionDownHandler(maskCanvasPoint);
            //     break;
            // }
        }
        if (updateView) {
            this.draw();
        }

        return updateView;
    }

    /**
     * @desc 鼠标抬起处理
     */
    mouseUpHandler(e, { mouseInToolPanel }) {
        this._isMouseDown = false;
        let updateView = false;

        if (this._isSlideCompare) {
            return;
        }
        if (this.isMove) {
            this._moveHandler(e);
            updateView = true;
        } else {
            switch (this.mouseEventType) {
                case MOUSE_EVENT_TYPE.MASK_DRAW: {
                    this.maskCanvasObject.stopDrawing();
                    break;
                }
                case MOUSE_EVENT_TYPE.MASK_ERASE:
                    break;

                case MOUSE_EVENT_TYPE.QUICK_SELECTION: {
                    if (mouseInToolPanel) return;
                    const maskCanvasPoint = {
                        x: this._mousePosition.x - this.originRect.x,
                        y: this._mousePosition.y - this.originRect.y,
                    };
                    updateView = this._quickSelectionUpHandler(maskCanvasPoint);
                    break;
                }
            }
        }
        if (updateView) {
            this.draw();
        }

        return updateView;
    }

    /**
     * @desc 画面移动处理
     * @param {*} e
     */
    _moveHandler(e) {
        const mousePosition = this.__getMousePosition(e);
        const moveDistance = {
            x: mousePosition.x - this._mousePosition.x,
            y: mousePosition.y - this._mousePosition.y,
        };
        const newOriginRect = {
            x: this.originRect.x + moveDistance.x,
            y: this.originRect.y + moveDistance.y,
            width: this.originRect.width,
            height: this.originRect.height,
        };

        this.setOriginRect(newOriginRect);
    }

    /**
     * @desc mask 绘制和擦除处理
     * @param {*} e
     */
    _maskDrawAndEraseHandler(e, addUndo) {
        const mousePosition = this.__getMousePosition(e);
        const maskSize = this.maskCanvasObject.getSize();
        const maskDrawSize = {
            width: this.originRect.width,
            height: this.originRect.height,
        };

        const zoomRatio = maskSize.width / maskDrawSize.width;

        const startPoint = {
            x: (this._mousePosition.x - this.originRect.x) * zoomRatio,
            y: (this._mousePosition.y - this.originRect.y) * zoomRatio,
        };

        const endPoint = {
            x: (mousePosition.x - this.originRect.x) * zoomRatio,
            y: (mousePosition.y - this.originRect.y) * zoomRatio,
        };

        let updateView = false;
        if (this.mouseEventType === MOUSE_EVENT_TYPE.MASK_DRAW) {
            updateView = this.maskCanvasObject.drawBrush(
                startPoint,
                endPoint,
                this._brushSize * this._zoom * zoomRatio * this.getRealCanvasRatio(),
                addUndo,
            );
        } else {
            updateView = this.maskCanvasObject.eraseBrush(
                startPoint,
                endPoint,
                this._brushSize * this._zoom * zoomRatio * this.getRealCanvasRatio(),
                addUndo,
            );
        }

        return updateView;
    }

    /**
     * @desc 移动处理
     * @param {*} e
     */
    mouseMoveHandler(e, mouseInToolPanel) {
        if (!this._isMouseDown && (this.mouseEventType !== MOUSE_EVENT_TYPE.QUICK_SELECTION || this.isMove)) return;

        let updateView = false;
        if (this.isMove) {
            this._moveHandler(e);
            updateView = true;
        } else {
            if (this._isSlideCompare) {
                return;
            }

            switch (this.mouseEventType) {
                case MOUSE_EVENT_TYPE.MASK_ERASE:
                case MOUSE_EVENT_TYPE.MASK_DRAW:
                    updateView = this._maskDrawAndEraseHandler(e);
                    break;
                case MOUSE_EVENT_TYPE.QUICK_SELECTION:
                    updateView = this._quickSelectionMoveHandler(e, mouseInToolPanel);
                    break;
            }
        }

        const mousePosition = this.__getMousePosition(e);
        this._mousePosition = mousePosition;
        if (updateView) {
            this.draw();
        }
    }

    /**
     * @desc 鼠标离开处理
     */
    mouseLeaveHandler() {
        const updateView = this.aiMaskCanvasObject?.mouseLeaveHandler();
        if (updateView) {
            this.draw();
        }
    }
    //#endregion

    //#region 绘制相关

    updateView() {
        if (this.isPhotoRemoveBg) {
            this._photoRemoveBgCanvasObject.updateView();
            this._photoRemoveBgCanvasObject.updateBackgroundCanvas();
        }
    }

    photoRemoveBgDraw(isCompare = false) {
        const isShowPhotoRemoveBg = this._mouseEventType === MOUSE_EVENT_TYPE.NONE;
        const editImageLoaded = this._photoRemoveBgCanvasObject.getEditImageLoaded();

        if (!isShowPhotoRemoveBg && isCompare) {
            const maskData = this._conversionMaskToPhotoRemoveBg();
            this._maskCanvasObject.setMaskData(maskData);

            this.maskCanvasObject.draw(this.viewCanvasContext, this.originRect);

            this.viewCanvasContext.save();
            this.viewCanvasContext.globalCompositeOperation = 'source-in';
            this._photoRemoveBgCanvasObject.draw(this.viewCanvasContext, this.originRect, {
                isCompare,
                slideCompare: this._isSlideCompare,
                compareLineScale: this._compareLineScale,
                isShowPhotoRemoveBg,
            });

            this.viewCanvasContext.restore();

            const __canvas = FJPoolStore.getCanvas(undefined, true);
            __canvas.width = this.originRect.width;
            __canvas.height = this.originRect.height;
            const ctx = __canvas.getContext('2d');

            FJCanvasUtil.drawTransparentBackground(ctx, this.originRect.width, this.originRect.height);

            this.viewCanvasContext.save();
            this.viewCanvasContext.globalCompositeOperation = 'destination-over';
            this.viewCanvasContext.drawImage(
                __canvas,
                this.originRect.x,
                this.originRect.y,
                this.originRect.width,
                this.originRect.height,
            );
            this.viewCanvasContext.restore();
            FJPoolStore.releaseCanvas(__canvas);

            return;
        }

        if (this._photoRemoveBgCanvasObject && editImageLoaded) {
            this._photoRemoveBgCanvasObject.draw(this.viewCanvasContext, this.originRect, {
                isCompare,
                slideCompare: this._isSlideCompare,
                compareLineScale: this._compareLineScale,
                isShowPhotoRemoveBg,
            });
        }

        if (!isShowPhotoRemoveBg) {
            if (
                this.mouseEventType !== MOUSE_EVENT_TYPE.NONE &&
                editImageLoaded &&
                !isCompare &&
                !this.isSlideCompare
            ) {
                this.maskCanvasObject.draw(this.viewCanvasContext, this.originRect);
            }
        }
    }

    /**
     * @desc 绘制视图画布
     */
    draw(isCompare = false) {
        if (!this.viewCanvas) return;
        this._viewCanvasContext.clearRect(0, 0, this.viewCanvas.width, this.viewCanvas.height);

        if (this.isPhotoRemoveBg) {
            this.photoRemoveBgDraw(isCompare);
            return;
        }

        this.originCanvasObject.draw(this.viewCanvasContext, this.originRect, {
            isCompare,
            slideCompare: this._isSlideCompare,
            compareLineScale: this._compareLineScale,
        });

        // 确保 编辑的图已经绘制 再绘制mask
        const getEditImageLoaded = this.originCanvasObject.getEditImageLoaded();
        if (this.mouseEventType !== MOUSE_EVENT_TYPE.NONE && getEditImageLoaded && !isCompare && !this.isSlideCompare) {
            this.maskCanvasObject.draw(this.viewCanvasContext, this.originRect);
        }

        if (this.aiMaskCanvasObject && !isCompare && !this.isSlideCompare) {
            this.aiMaskCanvasObject.draw(
                this.viewCanvasContext,
                this.originRect,
                this.mouseEventType === MOUSE_EVENT_TYPE.QUICK_SELECTION,
            );
        }
    }

    getMaskData(assignPhotoRemoveBg = false) {
        if ((this.isPhotoRemoveBg && this.mouseEventType === MOUSE_EVENT_TYPE.NONE) || assignPhotoRemoveBg) {
            return this._photoRemoveBgCanvasObject.getMaskData();
        }
        return this.maskCanvasObject.getMaskData();
    }

    setMaskData(maskData) {
        if (this.isPhotoRemoveBg) {
            this._photoRemoveBgCanvasObject.setMaskData(maskData);
        } else {
            this.maskCanvasObject.setMaskData(maskData);
        }
    }

    getAiMaskData() {
        return this.aiMaskCanvasObject?.getMaskData();
    }

    getAiMaskUndoRedoData() {
        return this.aiMaskCanvasObject?.getUndoRedoData();
    }

    setAiMaskUndoRedoData(undoRedoData) {
        this.aiMaskCanvasObject?.setUndoRedoData(undoRedoData);
    }

    /**
     * @desc 获取mask 图的url
     */
    getMaskImageUrl() {
        return this.maskCanvasObject.getImageUrl();
    }

    applyPhotoRemoveBgBrush() {
        this.addUndo(false, true);
        const imageData = this._conversionMaskToPhotoRemoveBg();
        this._photoRemoveBgCanvasObject.setMaskData(imageData);

        this._photoRemoveBgCanvasObject.updateView();
        this._photoRemoveBgCanvasObject.updateBackgroundCanvas();
        this.draw();
    }

    cancelPhotoRemoveBgBrush() {
        // 还原更改
        this.draw();
    }

    /**
     * @desc 将普通画笔模式下的mask 图  转为 去背景模式下的mask 图 保存在 maskCanvasObject 中
     * 进入去背景模式前初始化
     */
    _conversionMaskToPhotoRemoveBg(clearAiMask = true) {
        const maskData = this.getMaskData();
        const aiMaskData = this.getAiMaskData();

        if (!maskData && !aiMaskData) return;

        const originSize = this._photoRemoveBgCanvasObject.getSize();

        // 将mask 图转为黑白图
        const imageData = new ImageData(originSize.width, originSize.height);
        // const allImageData = new ImageData(originSize.width, originSize.height); // 合并 aiMask 和 mask 的图
        for (let i = 0; i < imageData.data.length; i += 4) {
            const alpha = maskData ? (maskData.data[i + 3] / 100) * 255 : -1;
            const aiAlpha = aiMaskData ? (aiMaskData.data[i + 3] * 10) / 4 : -1;

            const maxAlpha = Math.max(alpha, aiAlpha);

            if (maxAlpha > 0) {
                imageData.data[i] = 255; // R
                imageData.data[i + 1] = 255; // G
                imageData.data[i + 2] = 255; // B
                imageData.data[i + 3] = maxAlpha; // A

                // allImageData.data[i] = aiAlpha > 0 ? aiMaskData.data[i] : maskData.data[i]; // R
                // allImageData.data[i + 1] = aiAlpha > 0 ? aiMaskData.data[i + 1] : maskData.data[i + 1]; // G
                // allImageData.data[i + 2] = aiAlpha > 0 ? aiMaskData.data[i + 2] : maskData.data[i + 2]; // B
                // allImageData.data[i + 3] = aiAlpha > 0 ? aiMaskData.data[i + 3] : maskData.data[i + 3]; // A
            } else {
                imageData.data[i] = 0; // R
                imageData.data[i + 1] = 0; // G
                imageData.data[i + 2] = 0; // B
                imageData.data[i + 3] = 0; // A

                // allImageData.data[i] = 0; // R
                // allImageData.data[i + 1] = 0; // G
                // allImageData.data[i + 2] = 0; // B
                // allImageData.data[i + 3] = 0; // A
            }
        }

        // this.maskCanvasObject.setMaskData(allImageData);
        clearAiMask && this.aiMaskCanvasObject?.clear();
        return imageData;
    }

    /**
     * @desc 将去背景模式下的mask 图转为普通画笔模式下的mask 图
     */
    _conversionPhotoRemoveBgMaskToMask(assignPhotoRemoveBg = false) {
        const maskData = this.getMaskData(assignPhotoRemoveBg);

        const originSize = this._photoRemoveBgCanvasObject.getSize();

        const imageData = new ImageData(originSize.width, originSize.height);
        for (let i = 0; i < imageData.data.length; i += 4) {
            const alpha = maskData.data[i + 3];
            if (alpha > 0) {
                imageData.data[i] = 0;
                imageData.data[i + 1] = 0;
                imageData.data[i + 2] = 255;
                imageData.data[i + 3] = (alpha / 255) * 100;
            } else {
                imageData.data[i] = 0;
                imageData.data[i + 1] = 0;
                imageData.data[i + 2] = 0;
                imageData.data[i + 3] = 0;
            }
        }

        this.maskCanvasObject.setMaskData(imageData);
    }

    /**
     * @desc 获取mask 图的blob
     */
    async getMaskImageBlob() {
        const maskData = this.getMaskData();
        const aiMaskData = this.getAiMaskData();

        if (!maskData && !aiMaskData) return;

        const canvas = FJPoolStore.getCanvas(undefined, true);

        const originSize = this.originCanvasObject.getSize();
        canvas.width = originSize.width;
        canvas.height = originSize.height;

        // 将mask 图转为黑白图
        const imageData = new ImageData(originSize.width, originSize.height);
        for (let i = 0; i < imageData.data.length; i += 4) {
            const alpha = maskData ? maskData.data[i + 3] : -1;
            const aiAlpha = aiMaskData ? aiMaskData.data[i + 3] : -1;

            const maxAlpha = Math.max(alpha, aiAlpha);

            if (maxAlpha > 0) {
                imageData.data[i] = 255; // R
                imageData.data[i + 1] = 255; // G
                imageData.data[i + 2] = 255; // B
                imageData.data[i + 3] = 255; // A
            } else {
                imageData.data[i] = 0; // R
                imageData.data[i + 1] = 0; // G
                imageData.data[i + 2] = 0; // B
                imageData.data[i + 3] = 255; // A
            }
        }

        canvas.getContext('2d').putImageData(imageData, 0, 0);
        const blob = await canvas.convertToBlob();
        FJPoolStore.releaseCanvas(canvas);
        return blob;
    }

    getEditImageUrl() {
        if (this.isPhotoRemoveBg) {
            return this._photoRemoveBgCanvasObject.getImageUrl();
        }
        return this.originCanvasObject.getImageUrl();
    }

    async getEditImageBlob(maxSize) {
        if (this.isPhotoRemoveBg) {
            return await this._photoRemoveBgCanvasObject.getImageBlob(maxSize);
        }
        return await this.originCanvasObject.getImageBlob(maxSize);
    }

    async getRemoveBgImageBlob(maxSize) {
        if (!this.isPhotoRemoveBg) {
            if (!this._photoRemoveBgCanvasObject) {
                this._photoRemoveBgCanvasObject = new FJPhotoRemoveBgCanvas();
            }
            await this._photoRemoveBgCanvasObject.init(this.getEditImageUrl());

            this.aiMaskCanvasObject?.updateView(false, false);
            const imageData = this._conversionMaskToPhotoRemoveBg(false);
            this._photoRemoveBgCanvasObject.setMaskData(imageData);
        }

        return await this._photoRemoveBgCanvasObject.getRemoveBgImageBlob(maxSize);
    }

    async getOriginImageBlob(maxSize) {
        if (this.isPhotoRemoveBg) {
            return await this._photoRemoveBgCanvasObject.getOriginImageBlob(maxSize);
        }
        return await this.originCanvasObject.getOriginImageBlob(maxSize);
    }

    getOriginImageUrl() {
        if (this.isPhotoRemoveBg) {
            return this._photoRemoveBgCanvasObject.getOriginImageUrl();
        }
        return this.originCanvasObject.getOriginImageUrl();
    }

    getOriginImageLoaded() {
        if (this.isPhotoRemoveBg) {
            return this._photoRemoveBgCanvasObject.getOriginImageLoaded();
        }
        return this.originCanvasObject.getOriginImageLoaded();
    }

    compare(bool) {
        if (!bool && this.isPhotoRemoveBg && this.mouseEventType !== MOUSE_EVENT_TYPE.NONE) {
            this._conversionPhotoRemoveBgMaskToMask();
        }
        this.draw(bool);
    }

    enterSlideCompare(isUpdate = true) {
        this.isSlideCompare = true;
        isUpdate && this.draw();
    }

    endSlideCompare(isUpdate = true) {
        this.isSlideCompare = false;
        isUpdate && this.draw();
    }

    async drawMaskByImageUrl(imageUrl) {
        await this.maskCanvasObject.drawByImageUrl(imageUrl);
    }

    /**
     * @desc 获取mask使用情况
     */
    async getMaskUseSituation() {
        const maskIsUse = this.maskCanvasObject.isUse();
        const aiMaskIsUse = this.aiMaskCanvasObject?.isUse();
        return {
            maskIsUse,
            aiMaskIsUse,
        };
    }
    //#endregion

    //#region 计算相关

    /**
     * @desc 去背景模式下 进入画笔模式 mask-draw | mask-erase
     */
    enterBrushInPhotoRemoveBg() {
        this._conversionPhotoRemoveBgMaskToMask();
    }

    async enterPhotoRemoveBg() {
        try {
            if (this.isPhotoRemoveBg) return;

            if (!this._photoRemoveBgCanvasObject) {
                this._photoRemoveBgCanvasObject = new FJPhotoRemoveBgCanvas();
            }
            const editImageUrl = this.getEditImageUrl();
            this.isPhotoRemoveBg = true;

            if (!editImageUrl || !editImageUrl === '') return;
            await this._photoRemoveBgCanvasObject.init(editImageUrl);

            this.aiMaskCanvasObject?.updateView(false, false);
            const imageData = this._conversionMaskToPhotoRemoveBg();
            this._photoRemoveBgCanvasObject.setMaskData(imageData);

            this._photoRemoveBgCanvasObject.updateView();
            this._photoRemoveBgCanvasObject.updateBackgroundCanvas();
            this.draw();
        } catch (error) {
            // console.log('error', error);
        }
    }

    endPhotoRemoveBg(isDraw = true) {
        this.isPhotoRemoveBg = false;
        isDraw && this.draw();
    }

    initRect() {
        const originSize = this.isPhotoRemoveBg
            ? this._photoRemoveBgCanvasObject.getSize()
            : this.originCanvasObject.getSize();
        if (!originSize) return;

        const originRatio = originSize.width / originSize.height;

        let originRect = { x: 0, y: 0, width: 0, height: 0 };

        const initialViewCanvasRect = {
            width: this.viewCanvas.width - this._initImagePadding.width * this.getRealCanvasRatio(),
            height: this.viewCanvas.height - this._initImagePadding.height * this.getRealCanvasRatio(),
        };

        if (originRatio > 1) {
            originRect.width = initialViewCanvasRect.width;
            originRect.height = initialViewCanvasRect.width / originRatio;
        } else {
            originRect.height = initialViewCanvasRect.height;
            originRect.width = initialViewCanvasRect.height * originRatio;
        }

        // 如果originRect 大于initialViewCanvasRect，则缩小originRect
        if (originRect.width > initialViewCanvasRect.width) {
            originRect.width = initialViewCanvasRect.width;
            originRect.height = initialViewCanvasRect.width / originRatio;
        }
        if (originRect.height > initialViewCanvasRect.height) {
            originRect.height = initialViewCanvasRect.height;
            originRect.width = initialViewCanvasRect.height * originRatio;
        }

        // 如果图片的显示尺寸大于原始尺寸，则缩小originRect
        if (originRect.height > originSize.height) {
            originRect.width = originSize.width;
            originRect.height = originSize.width / originRatio;
        }
        if (originRect.height > originSize.height) {
            originRect.height = originSize.height;
            originRect.width = originSize.height * originRatio;
        }

        originRect.x = (this.viewCanvas.width - originRect.width) / 2;
        originRect.y = (this.viewCanvas.height - originRect.height) / 2 + this._offsetTop * this.getRealCanvasRatio();

        this.setOriginZoomSize({
            width: originRect.width,
            height: originRect.height,
        });
        this.setOriginRect(originRect);
    }

    __getMousePosition(e) {
        const canvasRect = this.viewCanvas.getBoundingClientRect();

        if (!e) {
            return {
                x: canvasRect.width / 2,
                y: canvasRect.height / 2,
            };
        }

        const { clientX, clientY } = this.__getClientPosition(e);
        const mousePosition = {
            x: (clientX - canvasRect.left) * this.getRealCanvasRatio(),
            y: (clientY - canvasRect.top) * this.getRealCanvasRatio(),
        };

        return mousePosition;
    }
    __getClientPosition(e) {
        if (e.clientX || e.clientY) {
            return { clientX: e.clientX, clientY: e.clientY };
        } else if ((e.targetTouches && e.targetTouches.length) || (e.touches && e.touches.length)) {
            // 移动端, (多指操作获取中心点位置)
            const touches = Array.from(e.targetTouches || e.touches);
            const clientX =
                touches.reduce((calc, cur) => {
                    return calc + cur.clientX;
                }, 0) / touches.length;
            const clientY =
                touches.reduce((calc, cur) => {
                    return calc + cur.clientY;
                }, 0) / touches.length;
            return { clientX, clientY };
        } else {
            return { clientX: 0, clientY: 0 };
        }
    }
    changeZoomFit(e) {
        const center = this.__getMousePosition(e);

        const preZoom = this.originRect.width / this.originZoomSize.width;

        let newOriginRect = {
            x: ((this.originRect.x - center.x) / preZoom) * this._zoom + center.x,
            y: ((this.originRect.y - center.y) / preZoom) * this._zoom + center.y,
            width: this.originZoomSize.width * this._zoom,
            height: this.originZoomSize.height * this._zoom,
        };

        // newOriginRect.x = center.x - newOriginRect.width / 2;
        // newOriginRect.y = center.y - newOriginRect.height / 2;

        this.setOriginRect(newOriginRect);
        this.draw();
    }

    /**
     * @desc 初始化比例和位置
     */
    fitZoomAndPosition() {
        let newOriginRect = {
            x: (this.viewCanvas.width - this.originZoomSize.width) / 2,
            y: (this.viewCanvas.height - this.originZoomSize.height) / 2 + this._offsetTop,
            width: this.originZoomSize.width,
            height: this.originZoomSize.height,
        };

        this.setOriginRect(newOriginRect);
        this.draw();
    }

    reset() {
        this.addUndo();
        if (this.isPhotoRemoveBg && this.mouseEventType === MOUSE_EVENT_TYPE.NONE) {
            this.setBackgroundInfo({ backgroundType: BACKGROUND_TYPE.TRANSPARENT });
            this.setStrokeInfo({ type: 'none' }, true);
        } else if (this.isPhotoRemoveBg) {
            this._conversionPhotoRemoveBgMaskToMask(true);
        } else {
            this.maskCanvasObject.reset();
            this.aiMaskCanvasObject?.reset();
        }
        this.draw();
    }

    resize() {
        this.setZoom(1, null, { zoomFit: false });
        this.viewCanvas.width = this.viewCanvas.getBoundingClientRect().width * this.getRealCanvasRatio();
        this.viewCanvas.height = this.viewCanvas.getBoundingClientRect().height * this.getRealCanvasRatio();
        this.initRect();
        this.draw();
    }

    clearAiMask() {
        this.aiMaskCanvasObject?.clear();
    }

    /**
     * @desc 是否可以缩放适配
     */
    isCanFit() {
        if (!this.originZoomSize || !this.originRect || this.originRect.width === 0 || this.originRect.height === 0)
            return false;

        let fitOriginRect = {
            x: (this.viewCanvas.width - this.originZoomSize.width) / 2,
            y: (this.viewCanvas.height - this.originZoomSize.height) / 2 + this._offsetTop,
            width: this.originZoomSize.width,
            height: this.originZoomSize.height,
        };

        const isMoveImage = this.originRect.x !== fitOriginRect.x || this.originRect.y !== fitOriginRect.y;

        return this._zoom !== 1 || isMoveImage;
    }

    clear() {
        this.maskCanvasObject.clear();
        this.aiMaskCanvasObject?.clear();
        this._photoRemoveBgCanvasObject?.clear();
        this.originCanvasObject.clear();
    }
    //#endregion

    //#region undo redo 相关
    addUndo(isRedo = false, assignPhotoRemoveBg = false) {
        if ((this.isPhotoRemoveBg && this.mouseEventType === MOUSE_EVENT_TYPE.NONE) || assignPhotoRemoveBg) {
            const maskData = this._photoRemoveBgCanvasObject.getMaskData();
            const backgroundInfo = this._photoRemoveBgCanvasObject.getBackgroundInfo();
            const strokeInfo = this._photoRemoveBgCanvasObject.getStrokeInfo();

            if (!maskData && !backgroundInfo && !strokeInfo) return;
            FJCreationEditUndoRedoManager.addUndo({ maskData, backgroundInfo, strokeInfo }, isRedo);
        } else {
            const maskData = this.getMaskData();
            const aiMaskUndoRedoData = this.aiMaskCanvasObject?.getUndoRedoData();

            if (!maskData && !aiMaskUndoRedoData) return;
            FJCreationEditUndoRedoManager.addUndo({ maskData, aiMaskUndoRedoData }, isRedo);
        }
    }

    addRedo() {
        if (this.isPhotoRemoveBg && this.mouseEventType === MOUSE_EVENT_TYPE.NONE) {
            const maskData = this._photoRemoveBgCanvasObject.getMaskData();
            const backgroundInfo = this._photoRemoveBgCanvasObject.getBackgroundInfo();
            const strokeInfo = this._photoRemoveBgCanvasObject.getStrokeInfo();
            if (!maskData && !backgroundInfo && !strokeInfo) return;
            FJCreationEditUndoRedoManager.addRedo({ maskData, backgroundInfo, strokeInfo });
        } else {
            const maskData = this.getMaskData();
            const aiMaskUndoRedoData = this.aiMaskCanvasObject?.getUndoRedoData();
            if (!maskData && !aiMaskUndoRedoData) return;
            FJCreationEditUndoRedoManager.addRedo({ maskData, aiMaskUndoRedoData });
        }
    }

    async undo() {
        this.addRedo();
        const undoRedoData = FJCreationEditUndoRedoManager.undo();
        if (!undoRedoData) return;

        if (this.isPhotoRemoveBg && this.mouseEventType === MOUSE_EVENT_TYPE.NONE) {
            this._photoRemoveBgCanvasObject.setMaskData(undoRedoData.maskData);
            this._photoRemoveBgCanvasObject.setStrokeInfo(undoRedoData.strokeInfo);
            await this.setBackgroundInfo(undoRedoData.backgroundInfo);
        } else {
            this.maskCanvasObject.setMaskData(undoRedoData.maskData);
            this.aiMaskCanvasObject?.setUndoRedoData(undoRedoData.aiMaskUndoRedoData);
            this.draw();
        }
    }

    async redo() {
        this.addUndo(true);
        const undoRedoData = FJCreationEditUndoRedoManager.redo();
        if (!undoRedoData) return;

        if (this.isPhotoRemoveBg && this.mouseEventType === MOUSE_EVENT_TYPE.NONE) {
            this._photoRemoveBgCanvasObject.setMaskData(undoRedoData.maskData);
            this._photoRemoveBgCanvasObject.setStrokeInfo(undoRedoData.strokeInfo);
            await this.setBackgroundInfo(undoRedoData.backgroundInfo);
        } else {
            this.maskCanvasObject.setMaskData(undoRedoData.maskData);
            this.aiMaskCanvasObject?.setUndoRedoData(undoRedoData.aiMaskUndoRedoData);
            this.draw();
        }
    }
    //#endregion

    //#region 属性设置相关

    setInitImagePadding(padding = INIT_IMAGE_PADDING) {
        this._initImagePadding = padding;
    }

    setOffsetTop(top = OFFSET_TOP) {
        this._offsetTop = top;
    }

    setStrokeInfo(strokeInfo, directExecute = false) {
        if (!this.isPhotoRemoveBg) return;

        if (!this._debouncedSetStrokeType) {
            this._debouncedSetStrokeType = _.debounce(
                strokeInfo => {
                    this._photoRemoveBgCanvasObject?.setStrokeInfo(strokeInfo);
                    this.draw();
                },
                100,
                { trailing: true },
            );
        }

        if (directExecute) {
            this._photoRemoveBgCanvasObject?.setStrokeInfo(strokeInfo);
            this.draw();
        } else {
            this._debouncedSetStrokeType(strokeInfo);
        }
    }

    getStrokeInfo() {
        if (!this.isPhotoRemoveBg) return null;
        return this._photoRemoveBgCanvasObject.getStrokeInfo();
    }

    async setBackgroundInfo(info) {
        if (!this.isPhotoRemoveBg) return;

        try {
            await this._photoRemoveBgCanvasObject.setBackgroundInfo(info);
            this.draw();
            return true;
        } catch (error) {
            return false;
        }
    }

    getBackgroundInfo() {
        if (!this.isPhotoRemoveBg) return null;
        return this._photoRemoveBgCanvasObject.getBackgroundInfo();
    }

    get isSlideCompare() {
        return this._isSlideCompare;
    }

    set isSlideCompare(value) {
        this._isSlideCompare = value;
    }

    get isPhotoRemoveBg() {
        return this._isPhotoRemoveBg;
    }

    set isPhotoRemoveBg(value) {
        this._isPhotoRemoveBg = value;
    }

    setAiMaskActive(value) {
        if (!this.aiMaskCanvasObject) return;
        this.aiMaskCanvasObject.setActive(value);
        this.draw();
    }

    getEditImageSize() {
        if (this.isPhotoRemoveBg) {
            return this._photoRemoveBgCanvasObject.getSize();
        }
        return this.originCanvasObject.getSize();
    }

    getOriginImageSize() {
        if (this.isPhotoRemoveBg) {
            return this._photoRemoveBgCanvasObject.getSize();
        }
        return this.originCanvasObject.getOriginSize();
    }

    setOriginRect(rect) {
        this.originRect = { ...rect };
    }

    setZoom(zoom, e, options = {}) {
        const { isFitClick = false, zoomFit = true } = options;
        this._zoom = zoom;

        zoomFit && !isFitClick && this.changeZoomFit(e);
        isFitClick && this.fitZoomAndPosition();
    }

    getZoom() {
        return this._zoom;
    }

    setOriginZoomSize(size) {
        this.originZoomSize = { ...size };
    }

    get mouseEventType() {
        return this._mouseEventType;
    }

    set mouseEventType(type) {
        this._mouseEventType = type;
    }

    setBrushSize(size) {
        this._brushSize = size;
    }

    getBrushSize() {
        return this._brushSize;
    }

    get viewCanvas() {
        return this._viewCanvas;
    }

    set viewCanvas(canvas) {
        this._viewCanvas = canvas;
    }

    get viewCanvasContext() {
        return this._viewCanvasContext;
    }

    set viewCanvasContext(context) {
        this._viewCanvasContext = context;
    }

    get originCanvasObject() {
        return this._originCanvasObject;
    }

    set originCanvasObject(object) {
        this._originCanvasObject = object;
    }

    get maskCanvasObject() {
        return this._maskCanvasObject;
    }

    set maskCanvasObject(object) {
        this._maskCanvasObject = object;
    }

    get originRect() {
        return this._originRect;
    }

    set originRect(rect) {
        this._originRect = rect;
    }

    get isMove() {
        return this._isMove;
    }

    set isMove(value) {
        this._isMove = value;
        this.aiMaskCanvasObject?.updateView(this._isMove);
        this.draw();
    }

    get compareLineScale() {
        return this._compareLineScale;
    }

    set compareLineScale(value) {
        this._compareLineScale = value;
    }

    /**
     * @desc 实际canvas 与视图canvas的缩放比例
     */
    getRealCanvasRatio() {
        if (!this.viewCanvas) return 1;

        return this.viewCanvas.width / this.viewCanvas.getBoundingClientRect().width;
    }
    //#endregion
}

const FJViewCanvasSingleton = new ViewCanvas();

export default ViewCanvas;
export { FJViewCanvasSingleton };
